import React, { useEffect, useRef } from "react";
import "./App.css";
import apple from "./assets/Apple_Music_icon.svg";
import tiktok from "./assets/TikTok_Logo.webp";
import instagram from "./assets/Instagram_logo_2016.svg";
import soundcloud from "./assets/soundcloudcircle.png";
import spotify from "./assets/Spotify_icon.png";
import sonicDunk from "./assets/sonic_dunk.png";

// Served from the public/ folder (same convention as the original hero image).
const bgImage = `${process.env.PUBLIC_URL}/expanded_valley.png`;

const chorus = {
  type: "chorus",
  lines: [
    "Ooh this abuse we get used to",
    "I told my heart",
    "it'll just take",
    "getting used to",
    "Wonder why I won't change",
    "getting used to",
  ],
};

const stanzas = [
  {
    type: "verse",
    lines: ["I tore myself apart", "so you could find", "something you liked"],
  },
  {
    type: "verse",
    lines: ["Looking for what", "could hold onto", "your elusive mind"],
  },
  {
    type: "verse",
    lines: [
      "But all I found",
      "were the pieces of me",
      "on the ground",
      "you walked on",
      "you had enough of me",
    ],
  },
  chorus,
  {
    type: "verse",
    lines: [
      "Now that my insides",
      "are deemed unsatisfactory",
      "How can I fix what",
      "seems impossibly broken",
      "I'll sew this back",
      "into my chest",
      "Mend the fabric I've awoken",
      "Look how the spool comes undone",
      "I'm unwoven",
    ],
  },
  chorus,
  {
    type: "bridge",
    lines: [
      "Ooh you go numb after a while",
      "Don't you",
      "Don't you",
      "Once you get",
      "used to it",
      "I'm used to it",
    ],
  },
  chorus,
];

function App() {
  const lyricsRef = useRef(null);

  useEffect(() => {
    const root = lyricsRef.current;
    if (!root) return;
    const lines = root.querySelectorAll(".lyric-line");
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // Reduced motion: show every line crisp, no observer.
    if (reduceMotion) {
      lines.forEach((line) => line.classList.add("is-unwoven"));
      return;
    }

    // Start woven, unravel each line as it scrolls into view.
    lines.forEach((line) => line.classList.remove("is-unwoven"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-unwoven");
        });
      },
      { threshold: 0.6, rootMargin: "0px 0px -10% 0px" },
    );
    lines.forEach((line) => observer.observe(line));

    // Reset the effect when the user returns to the top: re-weave every line
    // so it unravels again on the next scroll down.
    const onScroll = () => {
      if (window.scrollY <= 0) {
        lines.forEach((line) => line.classList.remove("is-unwoven"));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <>
      {/* Background layers: a blurred, enlarged copy "smart expands" to fill
          every edge, with the sharp full image centered on top. Applied via
          inline style so CRA/webpack resolves the imported asset URL. */}
      <div
        className="bg-layer bg-layer--blur"
        style={{ backgroundImage: `url(${bgImage})` }}
        aria-hidden="true"
      />
      <div
        className="bg-layer bg-layer--main"
        style={{ backgroundImage: `url(${bgImage})` }}
        aria-hidden="true"
      />

      {/* Fixed social nav — kept a top-level element (outside .main) so it
          isn't trapped in a content stacking context and stays clickable
          above the lyrics/footer while scrolling. */}
      <div className="social-icon-set">
        <a
          href="https://music.apple.com/us/artist/courtsie-co/1896637768"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img className="nav-icon-square" src={apple} alt="" />
        </a>

        <a
          href="https://open.spotify.com/artist/1L2DBynbBXbH0Bo8dxxm5z?si=8YjdC8VdSnW2BGRce_Odyw"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            className="nav-icon-square"
            src={spotify}
            alt="Courtsie Co on Spotify (opens in a new tab)"
          />
        </a>
        <a
          href="https://soundcloud.com/courtsieco"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            className="nav-icon"
            src={soundcloud}
            alt="Courtsie on SoundCloud (opens in a new tab)"
          />
        </a>
      </div>

      {/* Sticky decorative image pinned to the lower-right of the viewport. */}
      <img
        className="corner-sticky"
        src={sonicDunk}
        alt=""
        aria-hidden="true"
      />

      <div className="main">
        <div className="left"></div>
        <div className="center">
          <div className="hero-group">
            <h1 className="hero hero-main">
              Courtsie Co<span className="hero-period">.</span>
            </h1>
            {/* <a
              className="hero hero-btn"
              target="_blank"
              href="https://soundcloud.com/courtsieco/thevalley"
              rel="noopener noreferrer"
              aria-label="Listen to The Valley, Book I, on SoundCloud (opens in a new tab)"
            >
              Book I
            </a> */}
          </div>
        </div>
        <div className="right"></div>
      </div>

      <section className="lyrics" ref={lyricsRef} aria-label="Used To — lyrics">
        <div className="lyrics-head">
          <h2 className="hero lyrics-title">Used To</h2>
        </div>

        {stanzas.map((stanza, i) => (
          <div className={`stanza stanza-${stanza.type}`} key={i}>
            {stanza.lines.map((line, j) => (
              <p className="hero lyric-line" key={j}>
                {line.split(" ").map((word, k, arr) => (
                  <React.Fragment key={k}>
                    <span className="lyric-word">{word}</span>
                    {k < arr.length - 1 ? " " : ""}
                  </React.Fragment>
                ))}
              </p>
            ))}
          </div>
        ))}
      </section>

      <footer className="site-footer">
        <div className="footer-social">
          <a
            href="https://www.instagram.com/courtsieco"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              className="nav-icon-square"
              src={instagram}
              alt="Courtsie on Instagram (opens in a new tab)"
            />
          </a>
          <a
            href="https://www.tiktok.com/@courtsieco"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              className="nav-icon tiktok"
              src={tiktok}
              alt="Courtsie on TikTok (opens in a new tab)"
            />
          </a>
        </div>
        <p className="footer-text">
          &copy; {new Date().getFullYear()} Courtsie Co. All rights reserved.
        </p>
      </footer>
    </>
  );
}

export default App;
